/**
 * Adapters to convert backend DTOs to frontend chat types
 */
import {
  ChannelResponse,
  ChannelListItemResponse,
  ChannelTypeEnum,
  ChannelStatusEnum,
} from '../types/backend';
import { Conversation } from '../../types/chat';

/**
 * Convert backend ChannelResponse to frontend Conversation
 */
export const adaptChannelToConversation = (
  channel: ChannelResponse | ChannelListItemResponse,
  unreadCount: number = 0,
  currentUserId?: number
): Conversation => {
  const isListItem = 'read-only' in channel;

  // Check if this is a DM by type or by structure (2 members, no group name)
  const isDm =
    channel.type === ChannelTypeEnum.DM ||
    ('members' in channel &&
      channel.members.length === 2 &&
      !channel.name?.trim());

  // Generate channel name:
  // - For DM channels: use backend name if provided, otherwise generate from members
  // - For other channels: use backend name if provided, otherwise generate based on type
  const channelName =
    channel.name?.trim() || generateChannelName(channel, currentUserId);

  // Create lastMessage object if last-message-at exists
  const lastMessage = channel['last-message-at']
    ? {
        id: '', // We don't have the message ID in channel list
        from: '',
        to: '',
        type: 'text' as const,
        text: { body: '' },
        timestamp: new Date(channel['last-message-at']).getTime(),
        conversationId: String(channel.id),
        direction: 'incoming' as const,
      }
    : undefined;

  return {
    id: String(channel.id),
    type: isDm ? 'individual' : 'group',
    name: channelName,
    avatar: getChannelAvatar(channel, currentUserId),
    participants: isListItem
      ? [] // List items don't include full members
      : (channel as ChannelResponse).members.map((m) => String(m['user-id'])),
    unreadCount,
    lastMessage,
    isActive: channel.status === ChannelStatusEnum.ACTIVE,
    createdAt: new Date(channel['date-added']).getTime(),
    updatedAt: new Date(channel['date-updated']).getTime(),
    isInternal: channel.type === ChannelTypeEnum.INTERNAL,
    archived: channel.status === ChannelStatusEnum.ARCHIVED,
    groupSource: channel['group-source'] || undefined,
    whatsappStatus: ('whatsapp' in channel && (channel as ChannelResponse).whatsapp)
      ? (channel as ChannelResponse).whatsapp!.status
      : undefined,
    whatsappInviteLink: ('whatsapp' in channel && (channel as ChannelResponse).whatsapp)
      ? (channel as ChannelResponse).whatsapp!['invite-link']
      : undefined,
    whatsappMembers: ('whatsapp' in channel && (channel as ChannelResponse).whatsapp)
      ? (channel as ChannelResponse).whatsapp!.members.map((m) => ({
          id: m.id,
          phone: m.phone,
          displayName: m['display-name'],
          status: m.status,
          joinedAt: m['joined-at'],
        }))
      : undefined,
    account: channel.project?.project,
    createdByName: isListItem
      ? undefined
      : (channel as ChannelResponse).creator
        ? `${(channel as ChannelResponse).creator!.firstName} ${(channel as ChannelResponse).creator!.lastName}`
        : undefined,
  };
};

/**
 * Generate a default channel name for unnamed channels
 */
const generateChannelName = (
  channel: ChannelResponse | ChannelListItemResponse,
  currentUserId?: number
): string => {
  // Check if this is a DM by type or by having exactly 2 members
  const isDmByType = channel.type === ChannelTypeEnum.DM;
  const isDmByMembers =
    'members' in channel &&
    channel.members.length === 2 &&
    !channel.name?.trim();

  if (isDmByType || isDmByMembers) {
    // For DMs, use other participant's name if available
    if ('members' in channel && channel.members.length > 0) {
      // Find the other participant (not the current user)
      const otherMember = channel.members.find(
        (m) => m.user && (!currentUserId || m['user-id'] !== currentUserId)
      );
      if (otherMember?.user) {
        return `${otherMember.user.firstName} ${otherMember.user.lastName}`;
      }

      // Fallback: try to find any member with user info
      const anyMemberWithUser = channel.members.find((m) => m.user);
      if (anyMemberWithUser?.user) {
        return `${anyMemberWithUser.user.firstName} ${anyMemberWithUser.user.lastName}`;
      }
    }

    // For list items or DMs without member data, return generic name
    // Note: Backend should populate the name field for DM list items
    return 'Direct Message';
  }

  if (channel.type === ChannelTypeEnum.CLIENT && channel.project) {
    return `${channel.project.project} - Discussion`;
  }

  return 'Unnamed Channel';
};

/**
 * Get avatar URL for channel
 */
const getChannelAvatar = (
  channel: ChannelResponse | ChannelListItemResponse,
  currentUserId?: number
): string | undefined => {
  // For DMs (by type or structure), use other participant's avatar
  const isDmByType = channel.type === ChannelTypeEnum.DM;
  const isDmByMembers =
    'members' in channel &&
    channel.members.length === 2 &&
    !channel.name?.trim();

  if ((isDmByType || isDmByMembers) && 'members' in channel) {
    const otherMember = channel.members.find(
      (m) => m.user && (!currentUserId || m['user-id'] !== currentUserId)
    );
    if (otherMember?.user?.avatar) {
      return otherMember.user.avatar;
    }

    // Fallback: return any member's avatar
    const anyMemberWithAvatar = channel.members.find((m) => m.user?.avatar);
    if (anyMemberWithAvatar?.user?.avatar) {
      return anyMemberWithAvatar.user.avatar;
    }
  }

  // For channels, use creator's avatar or first member's avatar
  if ('creator' in channel && channel.creator) {
    return channel.creator.avatar;
  }

  if (
    'members' in channel &&
    channel.members.length > 0 &&
    channel.members[0].user
  ) {
    return channel.members[0].user.avatar;
  }

  return undefined;
};

/**
 * Batch convert channels with unread counts
 */
export const adaptChannelsWithUnreads = (
  channels: ChannelListItemResponse[],
  unreadCounts: Record<string, number>,
  currentUserId?: number
): Conversation[] => {
  return channels.map((channel) =>
    adaptChannelToConversation(
      channel,
      unreadCounts[channel.id] || 0,
      currentUserId
    )
  );
};
